import { parseLfsPointerBytes } from './gitLfsPointer';
import {
  isLikelyVideoContainerBytes,
  isMp3Bytes,
  mimeForMediaPlaybackPath,
  shouldUseAudioPlayback,
} from './itemMediaPlayback';

export type MediaBytesProbe = {
  byteLength: number;
  hexHead: string;
  textHead: string;
  isLfsPointer: boolean;
  lfsOid?: string;
  lfsSize?: number;
  isMp4Ftyp: boolean;
  isMp3: boolean;
  useAudioElement: boolean;
  suggestedMime: string;
  looksPlayable: boolean;
  /** MP4 sample entry fourccs found in the file (e.g. avc1, hvc1). */
  mp4Codecs?: string[];
  /** True when moov atom precedes mdat (fast start — better for streaming). */
  mp4FastStart?: boolean;
  /** Fatal problem — do not assign to media element. */
  issue?: string;
  /** Non-fatal note (codec compatibility, slow start). */
  warning?: string;
};

const MP4_CODEC_TAGS = ['avc1', 'avc3', 'hvc1', 'hev1', 'hvt1', 'mp4a', 'ac-3', 'ec-3'] as const;
const HEVC_TAGS = new Set(['hvc1', 'hev1', 'hvt1']);

/** Scan the start of an MP4 for common sample-entry fourccs. */
function scanBytesForCodecTags(bytes: Uint8Array, start: number, end: number): string[] {
  const found: string[] = [];
  let text = '';
  try {
    text = new TextDecoder('latin1').decode(bytes.subarray(start, end));
  } catch {
    return found;
  }
  for (const tag of MP4_CODEC_TAGS) {
    if (text.includes(tag)) found.push(tag);
  }
  return found;
}

export function sniffMp4Codecs(bytes: Uint8Array, maxScan = 512 * 1024): string[] {
  const scanLen = Math.min(bytes.length, maxScan);
  const head = scanBytesForCodecTags(bytes, 0, scanLen);
  const tailStart = Math.max(0, bytes.length - scanLen);
  const tail = tailStart > 0 ? scanBytesForCodecTags(bytes, tailStart, bytes.length) : [];
  return [...new Set([...head, ...tail])];
}

/** True when the moov atom appears before mdat (fast start). */
export function mp4HasFastStart(bytes: Uint8Array, maxScan = 2 * 1024 * 1024): boolean | null {
  const scanLen = Math.min(bytes.length, maxScan);
  let text = '';
  try {
    text = new TextDecoder('latin1').decode(bytes.subarray(0, scanLen));
  } catch {
    return null;
  }
  const moov = text.indexOf('moov');
  const mdat = text.indexOf('mdat');
  if (moov < 0 && mdat < 0) return null;
  if (moov < 0) return false;
  if (mdat < 0) return true;
  return moov < mdat;
}

export function mp4UsesHevc(codecs: string[]): boolean {
  return codecs.some(c => HEVC_TAGS.has(c));
}

function hexPrefix(bytes: Uint8Array, n = 16): string {
  const slice = bytes.subarray(0, Math.min(n, bytes.length));
  return [...slice].map(b => b.toString(16).padStart(2, '0')).join(' ');
}

function textPrefix(bytes: Uint8Array, n = 80): string {
  const slice = bytes.subarray(0, Math.min(n, bytes.length));
  try {
    return new TextDecoder().decode(slice).replace(/\s+/g, ' ').trim();
  } catch {
    return '';
  }
}

/** Inspect downloaded bytes to explain why playback may fail. */
export function probeMediaBytes(bytes: Uint8Array, repoPath: string): MediaBytesProbe {
  const filename = repoPath.split('/').pop() ?? '';
  const ptr = parseLfsPointerBytes(bytes);
  const isLfsPointer = ptr != null;
  const isMp4Ftyp = isLikelyVideoContainerBytes(bytes);
  const isMp3 = isMp3Bytes(bytes);
  const useAudioElement = shouldUseAudioPlayback(filename, bytes);
  const suggestedMime = mimeForMediaPlaybackPath(repoPath, bytes);

  let looksPlayable = false;
  let issue: string | undefined;

  if (bytes.length === 0) {
    issue = 'empty payload (0 bytes)';
  } else if (isLfsPointer) {
    issue = `LFS pointer only (${bytes.length} bytes) — real media was not resolved`;
  } else if (isMp3 && !useAudioElement && /\.(mp4|webm|mov)$/i.test(filename)) {
    issue = 'MP3 audio mislabeled with video extension';
    looksPlayable = true;
  } else if (isMp3 || useAudioElement) {
    looksPlayable = bytes.length > 128;
  } else if (isMp4Ftyp) {
    looksPlayable = bytes.length > 256;
    const mp4Codecs = sniffMp4Codecs(bytes);
    const mp4FastStart = mp4HasFastStart(bytes);
    let warning: string | undefined;
    if (mp4UsesHevc(mp4Codecs) && !mp4Codecs.some(c => c.startsWith('avc'))) {
      warning =
        'HEVC/H.265 codec — plays in Safari but often not in Chrome or Firefox; re-export as H.264';
    } else if (mp4FastStart === false) {
      warning = 'MP4 slow start (moov after mdat); may take a moment to buffer';
    }
    return {
      byteLength: bytes.length,
      hexHead: hexPrefix(bytes),
      textHead: textPrefix(bytes),
      isLfsPointer: false,
      isMp4Ftyp,
      isMp3,
      useAudioElement,
      suggestedMime,
      looksPlayable,
      ...(mp4Codecs.length > 0 ? { mp4Codecs } : {}),
      ...(mp4FastStart != null ? { mp4FastStart } : {}),
      ...(warning ? { warning } : {}),
    };
  } else if (/\.(webm|mkv|ogv)$/i.test(filename)) {
    looksPlayable = bytes.length > 256;
  } else {
    issue = `unrecognized media header (first bytes: ${hexPrefix(bytes, 8)})`;
  }

  return {
    byteLength: bytes.length,
    hexHead: hexPrefix(bytes),
    textHead: textPrefix(bytes),
    isLfsPointer,
    ...(ptr ? { lfsOid: ptr.oid, lfsSize: ptr.size } : {}),
    isMp4Ftyp,
    isMp3,
    useAudioElement,
    suggestedMime,
    looksPlayable,
    ...(issue ? { issue } : {}),
  };
}

/**
 * PAT raw URLs are cross-origin; fetch probes are CORS-blocked even when a video
 * element can stream them. Assume playable from path extension when PAT is present.
 */
export function optimisticPrivateStreamProbe(repoPath: string): MediaBytesProbe {
  const filename = repoPath.split('/').pop() ?? '';
  const isMp4 = /\.mp4$/i.test(filename);
  return {
    byteLength: 0,
    hexHead: '',
    textHead: '',
    isLfsPointer: false,
    isMp4Ftyp: isMp4,
    isMp3: /\.mp3$/i.test(filename),
    useAudioElement: /\.(mp3|m4a|wav|ogg)$/i.test(filename),
    suggestedMime: mimeForMediaPlaybackPath(repoPath),
    looksPlayable: /\.(mp4|webm|mov|m4v|mp3|m4a|wav|ogg)$/i.test(filename),
  };
}
